import { useState, useRef } from 'react';
import Papa from 'papaparse';
import { bulkImportClients } from '../lib/supabase';

const CSV_HEADERS = [
  'client_id','first_name','last_name','date_of_birth','gender','language',
  'address','city','county','state','zip','medicaid_id','private_insurance',
  'guardian_first_name','guardian_last_name','guardian_relationship','guardian_phone','guardian_email','guardian_address',
  'emergency_first_name','emergency_last_name','emergency_relationship','emergency_phone','emergency_email','emergency_address',
  'diagnosis_summary','full_diagnoses','doctor_name','doctor_practice',
  'doctor_address','doctor_phone','current_status','referral_source',
  'referral_date','responsible_person_name',
  'nurse_assessment_complete',
  'medicaid_eligible','eligibility_checked_date','eligibility_checked_by',
  'client_type',
];

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResults(null);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      preview: 5,
      complete: (result) => {
        setHeaders(result.meta.fields || []);
        setPreview(result.data);
      },
    });
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const records = result.data.map(row => ({
          ...row,
          private_insurance: row.private_insurance === 'Yes' || row.private_insurance === 'true',
          date_of_birth: row.date_of_birth || null,
          referral_date: row.referral_date || null,
          current_status: row.current_status || 'New Referral',
          intake_complete: true,
        }));
        const res = await bulkImportClients(records);
        setResults(res);
        setImporting(false);
      },
    });
  };

  const downloadTemplate = () => {
    const csv = CSV_HEADERS.join(',') + '\n' +
      'CPHC001,Jon,Doe,2012-08-31,Male,English,2 Berry Dr SW,Cartersville,Cherokee,Georgia,30121,222222222222,No,Parent Name,Mom,470-555-0000,email@example.com,Emergency Name,Father,470-555-0001,Down syndrome,Down syndrome - oxygen dependent,Dr Smith,Smith Pediatrics,123 Main St Cartersville GA,706-555-0000,New Referral,CHOA,2025-12-09,Tiffany Tinoco,N,Pending,,,New';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'ahfc_import_template.csv'; a.click();
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Step 1 — Download Template</div></div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Download the CSV template, fill in your existing client data, then upload it below. The template includes all required column headers. Client IDs like CPHC001 will be preserved as-is. Leave client_id blank for new records and they will be auto-generated.
          </p>
          <button className="btn btn-outline" onClick={downloadTemplate}>
            ↓ Download CSV Template
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Step 2 — Upload CSV File</div></div>
        <div className="card-body">
          <div
            style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer', transition: 'border 0.15s' }}
            onClick={() => fileRef.current.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--accent)'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--border)'; const f = e.dataTransfer.files[0]; if (f) { setFile(f); fileRef.current.files = e.dataTransfer.files; handleFile({ target: { files: [f] } }); } }}
          >
            <svg width="32" height="32" fill="none" stroke="var(--text3)" strokeWidth="1.5" viewBox="0 0 24 24" style={{ margin: '0 auto 12px', display: 'block' }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>
              {file ? file.name : 'Click to upload or drag & drop'}
            </p>
            <p style={{ fontSize: 12, color: 'var(--text3)' }}>CSV files only</p>
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
          </div>
        </div>
      </div>

      {preview.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">Step 3 — Preview ({preview.length} rows shown)</div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>{headers.slice(0, 8).map(h => <th key={h}>{h}</th>)}{headers.length > 8 && <th>+{headers.length - 8} more</th>}</tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    {headers.slice(0, 8).map(h => <td key={h} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row[h] || '—'}</td>)}
                    {headers.length > 8 && <td style={{ color: 'var(--text3)', fontSize: 12 }}>...</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-outline" onClick={() => { setFile(null); setPreview([]); setHeaders([]); fileRef.current.value = ''; }}>Clear</button>
            <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
              {importing ? <><span className="loading-spinner" style={{ borderTopColor: '#fff' }} /> Importing...</> : 'Import All Records'}
            </button>
          </div>
        </div>
      )}

      {results && (
        <div className={`card`} style={{ border: `1px solid ${results.errors.length > 0 ? 'var(--warn-border)' : 'var(--success-border)'}` }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: results.errors.length > 0 ? 16 : 0 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: results.errors.length > 0 ? 'var(--warn-bg)' : 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {results.errors.length > 0
                  ? <svg width="18" height="18" fill="none" stroke="var(--warn)" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  : <svg width="18" height="18" fill="none" stroke="var(--success)" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                }
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  Import complete — {results.success} records imported successfully
                </div>
                {results.errors.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: 2 }}>{results.errors.length} records failed</div>
                )}
              </div>
            </div>
            {results.errors.length > 0 && (
              <div style={{ background: 'var(--warn-bg)', borderRadius: 8, padding: 12 }}>
                {results.errors.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 4 }}>
                    <strong>{e.record}</strong>: {e.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
